import React from 'react';

import { Box } from '@betterment/samba-react';
import { Helmet } from 'react-helmet';

import NoticeCollection from '../../InAppMessages/GlobalNotices/NoticeCollection';
import { Notifications } from '../../notifications/Notifications';
import { WithLayout } from '../../RetailApp/components/WithLayout';
import Footer from '../components-v2/footer/Footer';

interface LayoutProps {
  feature: string;
  pageTitle: string | null;
}

const Layout: React.FC<React.PropsWithChildren<LayoutProps>> = ({ children, feature, pageTitle }) => (
  <WithLayout parts={{ sidebar: true, footer: false, globalNotice: false }} feature={feature}>
    <Box
      css={{
        bg: 'background',
        display: 'grid',
        gridTemplateColumns: '1fr minmax(min-content, 1200px) 1fr',
        gridTemplateAreas: `
          "notifications notifications notifications"
          "header header header"
          ". content ."
          ". footer ."
        `,
        gridColumnGap: {
          base: 'space.0',
          md: 'space.400',
        },
      }}
    >
      <Helmet titleTemplate="%s | Betterment" defaultTitle="Betterment">
        <title>{pageTitle}</title>
      </Helmet>
      {children}
      <Box
        css={{
          gridArea: 'notifications',
          display: 'grid',
          gridTemplateColumns: 'auto minmax(min-content, 1200px) auto',
          gap: 'space.400',
          rowGap: 'space.0',
          // pages without a header should have a grey background so we use a css selector
          // ideally we would use this selector:
          // '&:has(~ #application-layout-header)': { bg: 'background.surface' },
          // but has is not 100% supported yet. we can use a simple siblings selector after moving notifications under content
          // since we use a grid template that guarantees visual order
          '#application-layout-header ~ &': { bg: 'background.surface' },
        }}
      >
        <NoticeCollection css={{ gridColumnStart: 2 }} />
        <Notifications css={{ gridColumnStart: 2 }} />
      </Box>
      <Box
        css={{
          gridArea: 'footer',
          maxWidth: '1200px',
          paddingX: {
            sm: 'space.400',
            md: 'space.0',
          },
        }}
      >
        <Footer />
      </Box>
    </Box>
  </WithLayout>
);

const Header: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    id="application-layout-header"
    css={{
      bg: 'background.surface',
      gridArea: 'header',
      display: 'grid',
      gridTemplateColumns: 'subgrid',
      marginBottom: {
        base: 'space.200',
        md: 'space.400',
      },
    }}
  >
    <Box
      css={{
        gridColumn: 2,
      }}
    >
      {children}
    </Box>
  </Box>
);

const Content: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    css={{
      bg: 'background',
      gridArea: 'content',
    }}
  >
    {children}
  </Box>
);

type ApplicationLayoutComposite = typeof Layout & {
  Header: typeof Header;
  Content: typeof Content;
};
export const ApplicationLayout = Layout as ApplicationLayoutComposite;
ApplicationLayout.Header = Header;
ApplicationLayout.Content = Content;
